import { useState, useContext } from "react"

import { db } from "../../../utils/firebase"
import { ModalContext } from "../../../utils/providers/modal"

// Reuse the per-round tee-time editor from the build flow (slice 5) so the
// add/remove row UI is identical here and in the quoting wizard.
import TeeTimes from "../../quoting/stages/configure/package/tee-times"

// The reusable PDF builder (slice 5) + the shared quote-send email (slice 6).
import { buildQuotePDF, pdfBytesToBase64 } from "../../quoting/rows/pdf-utils/build-quote-pdf"
import { sendQuoteEmail, resolveCurrentAgent } from "../../quoting/utils/send-quote-email"

// UI components
import Button from "../../../components/ui/button/button"

// Generate a throwaway id for a freshly-added tee-time row (matches the build
// flow's row id pattern so re-rendered rows stay keyed consistently).
const generateRowID = () => Math.random().toString(36).substr(2, 9)

// Normalise a round's tee-time rows so every row has a stable _id for keying
// (older stored rows may pre-date the _id field).
const withRowIDs = (teeTimes) => (Array.isArray(teeTimes) ? teeTimes.map((row) => ({ _id: row._id || generateRowID(), ...row })) : [])

/**
 * Post-generation tee-time editor for a single generated quote package on the
 * enquiry Quotes tab.
 *
 * Tee times are per round (display-only - no pricing impact). Saving writes the
 * edited rows back to the round's `tee_times` on `quotes/{id}`, regenerates that
 * package's PDF via the shared builder (updating
 * `quotes/{id}/documents/{reference}` + the package `pdf_url`), then - because
 * the quote may already have been sent - PROMPTS the agent to resend the
 * updated quote (it never auto-resends; declining just keeps the stored PDF).
 *
 * `quote` is the full quotes/{id} document (we need its packages array, client
 * and any stored alternative_pricing). `quotePackage` is the single package
 * being edited (one entry of quote.packages). `clientName` is the greeting for
 * the regenerated PDF. `agent` is the (optionally pre-fetched) agent doc.
 * `onClose` collapses the editor; `onRegenerated` lets the parent refresh the
 * link to the new PDF.
 */
export default function QuoteTeeTimesEditor({ quote, quotePackage, clientName, clientEmail, agent, onClose, onRegenerated }) {
	const { showModal } = useContext(ModalContext)

	// Working copy of the package's sub-packages with normalised tee-time ids.
	// Each sub-package is either a resort (per-round tee times inside
	// chosen_courses) or a standalone course (a single package-level list).
	const [subPackages, setSubPackages] = useState(() =>
		(quotePackage.packages || []).map((subPackage) => {
			if (subPackage.price_type === "resort") {
				return {
					...subPackage,
					chosen_courses: (subPackage.chosen_courses || []).map((course) => ({
						...course,
						tee_times: withRowIDs(course.tee_times),
					})),
				}
			}

			// Standalone course - a single round held on the sub-package itself.
			return { ...subPackage, tee_times: withRowIDs(subPackage.tee_times) }
		})
	)

	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)

	// Update the tee times for one resort round (by sub-package + course index).
	const updateRoundTeeTimes = (subIndex, courseIndex, updatedTeeTimes) => {
		setSubPackages((current) =>
			current.map((subPackage, index) => {
				if (index !== subIndex) return subPackage

				const clonedCourses = [...subPackage.chosen_courses]
				clonedCourses[courseIndex] = { ...clonedCourses[courseIndex], tee_times: updatedTeeTimes }
				return { ...subPackage, chosen_courses: clonedCourses }
			})
		)
	}

	// Update the tee times for a standalone course sub-package.
	const updateCourseTeeTimes = (subIndex, updatedTeeTimes) => {
		setSubPackages((current) => current.map((subPackage, index) => (index === subIndex ? { ...subPackage, tee_times: updatedTeeTimes } : subPackage)))
	}

	// Strip the throwaway _id off rows before persisting so we don't write UI
	// scaffolding into Firestore (the build flow's saved rows carry _id too, so
	// this is purely belt-and-braces and keeps stored data tidy).
	const cleanTeeTimes = (teeTimes) => (teeTimes || []).map(({ _id, ...rest }) => rest)

	// Build the persisted version of the edited package (sub-packages with their
	// edited tee times folded back in).
	const buildUpdatedPackage = () => {
		const cleanedSubPackages = subPackages.map((subPackage) => {
			if (subPackage.price_type === "resort") {
				return {
					...subPackage,
					chosen_courses: (subPackage.chosen_courses || []).map((course) => ({
						...course,
						tee_times: cleanTeeTimes(course.tee_times),
					})),
				}
			}

			return { ...subPackage, tee_times: cleanTeeTimes(subPackage.tee_times) }
		})

		return { ...quotePackage, packages: cleanedSubPackages }
	}

	// Offer to resend the updated quote. Never auto-resends - declining simply
	// keeps the freshly-regenerated stored PDF.
	const promptResend = (updatedPackage, pdfBytes) => {
		showModal({
			type: "SUCCESS",
			title: "Quote PDF updated",
			body: `The PDF for ${updatedPackage.reference} has been regenerated with the new tee times. Would you like to resend the updated quote to ${clientEmail || clientName}?`,
			cancel: { label: "Not now", action: () => null },
			next: { label: "Resend quote", action: () => resendQuote(updatedPackage, pdfBytes) },
		})
	}

	// Resend the regenerated PDF to the client (reuses the shared quote-send
	// email helper, the single source of truth for the quote-send shape).
	const resendQuote = async (updatedPackage, pdfBytes) => {
		try {
			const sendingAgent = agent && agent.email ? { id: quote.agent, name: agent.name || `${agent.first_name || ""} ${agent.last_name || ""}`.trim(), first_name: agent.first_name, phone: agent.phone, email: agent.email } : await resolveCurrentAgent()

			const pdfBytesBase64 = await pdfBytesToBase64(pdfBytes)

			await sendQuoteEmail({
				reference: updatedPackage.reference,
				pdfBytesBase64,
				clientID: quote.client,
				clientName,
				clientEmail,
				agent: sendingAgent,
			})

			showModal({
				type: "SUCCESS",
				title: "Quote resent",
				body: `The updated quote ${updatedPackage.reference} has been resent to the client.`,
				cancel: { label: "Close", action: () => null },
				next: {},
			})
		} catch (resendError) {
			console.error("Error resending updated quote:", resendError)
			showModal({
				type: "ERROR",
				title: "Could not resend",
				body: "The PDF was updated, but the email could not be resent. Please try again.",
				cancel: { label: "Close", action: () => null },
				next: {},
			})
		}
	}

	// Save the edited tee times: persist back to quotes/{id}, regenerate the
	// package PDF, then prompt the agent to resend.
	const saveTeeTimes = async () => {
		setSaving(true)
		setError(null)

		try {
			const editedPackage = buildUpdatedPackage()

			// 1) Regenerate this package's PDF (writes quotes/{id}/documents/{ref}
			//    + uploads to storage). Reuse any stored alternative pricing so the
			//    indicative comparison line is preserved on the regenerated PDF.
			const { url, bytes } = await buildQuotePDF({
				quoteID: quote.id,
				details: editedPackage,
				agent: agent && agent.email ? { ...agent, name: agent.name || `${agent.first_name || ""} ${agent.last_name || ""}`.trim() } : null,
				clientName,
				customIntroduction: DEFAULT_INTRODUCTION,
				replaceFields: [{ from: "", to: "" }],
				offsetYear: 0,
				customDeposit: editedPackage.custom_deposit,
				alternativePricing: quote.alternative_pricing || null,
			})

			// Stamp the freshly-regenerated PDF url onto the package.
			const updatedPackage = { ...editedPackage, pdf_url: url }

			// 2) Persist the edited tee times + new pdf_url back onto the quote
			//    document. Replace this package within the full packages array
			//    (matched on reference, which is unique per package per quote).
			const updatedPackages = (quote.packages || []).map((existingPackage) => (existingPackage.reference === updatedPackage.reference ? updatedPackage : existingPackage))

			await db.doc(`quotes/${quote.id}`).set({ packages: updatedPackages }, { merge: true })

			setSaving(false)

			// Let the parent refresh the link to the new PDF if it wants to.
			if (onRegenerated) onRegenerated(updatedPackage.reference, url)

			// 3) Prompt to resend (never auto-resends).
			promptResend(updatedPackage, bytes)
		} catch (saveError) {
			console.error("Error saving tee times / regenerating PDF:", saveError)
			setSaving(false)
			setError("Could not save the tee times. Please try again.")
		}
	}

	return (
		<div className="quote-tee-times-editor">
			{subPackages.map((subPackage, subIndex) => (
				<div className="quote-tee-times-package" key={subIndex}>
					{subPackage.price_type === "resort" && subPackage.chosen_courses?.length > 0 && (
						<>
							<p className="quote-tee-times-package-title">{subPackage.parent_title}</p>

							{subPackage.chosen_courses.map((course, courseIndex) => (
								<TeeTimes
									key={courseIndex}
									label={`Round ${courseIndex + 1}${course.name ? ` (${course.name})` : ""}`}
									teeTimes={course.tee_times || []}
									onChange={(updated) => updateRoundTeeTimes(subIndex, courseIndex, updated)}
								/>
							))}
						</>
					)}

					{subPackage.price_type === "course" && (
						<>
							<p className="quote-tee-times-package-title">
								{subPackage.parent_title} - {subPackage.name}
							</p>

							<TeeTimes label="Round" teeTimes={subPackage.tee_times || []} onChange={(updated) => updateCourseTeeTimes(subIndex, updated)} />
						</>
					)}
				</div>
			))}

			{error && <p className="quote-tee-times-error">{error}</p>}

			<div className="quote-tee-times-actions">
				<Button type="secondary" small={true} label="Cancel" onClick={() => onClose()} disabled={saving} />
				<Button small={true} label="Save &amp; regenerate" loading={saving} loadingText="Regenerating PDF..." onClick={() => saveTeeTimes()} />
			</div>
		</div>
	)
}

// The default introduction paragraph used when regenerating a quote PDF after
// the fact (the original intro text isn't stored on the quote doc). Mirrors the
// default in stages/generating-pdf.js so a regenerated PDF reads the same.
const DEFAULT_INTRODUCTION =
	"Thank you for your recent enquiry with Golf Holidays Direct. As one of the leading providers in the UK we take pride in ensuring an unforgettable experience for all clients from the moment they enquire, so we hope you have had a pleasant experience with us so far.\n\nWithin this document, you'll find a full breakdown of your proposed trip, a summary of all costs associated and a tentative itinerary for us to review and make changes too."
